import { useQuery } from "@tanstack/react-query";
import {
	ArrowLeftRight,
	CircleAlert,
	CircleCheck,
	CircleMinus,
	Clock3,
	LoaderCircle,
	TriangleAlert,
} from "lucide-react";
import { useState } from "react";

import { api, type ReceiptList } from "@/client";
import { MetricCard } from "@/components/MetricCard";
import { FinancePresentation } from "@/presentation/finance";

type ReceiptGroup = ReceiptList["groups"][number];
type ReceiptStatus = ReceiptGroup["status"];
type StatusFilter = "ALL" | ReceiptStatus;

const statusPresentation: Record<
	ReceiptStatus,
	{ label: string; className: string; icon: React.ReactNode }
> = {
	RECONCILED: {
		label: "Conciliado",
		className: "border-emerald-200 bg-emerald-50 text-emerald-800",
		icon: <CircleCheck className="size-3.5" />,
	},
	AWAITING_GUARDIAN: {
		label: "Aguardando Guardian",
		className: "border-amber-200 bg-amber-50 text-amber-800",
		icon: <Clock3 className="size-3.5" />,
	},
	REVIEW_REQUIRED: {
		label: "Revisar",
		className: "border-red-200 bg-red-50 text-red-800",
		icon: <TriangleAlert className="size-3.5" />,
	},
	NOT_RECEIVABLE: {
		label: "Não é recebimento de cliente",
		className: "border-sky-200 bg-sky-50 text-sky-800",
		icon: <CircleMinus className="size-3.5" />,
	},
};

const statusFilters: ReceiptStatus[] = [
	"RECONCILED",
	"AWAITING_GUARDIAN",
	"REVIEW_REQUIRED",
	"NOT_RECEIVABLE",
];

export function ReceiptsView() {
	const [status, setStatus] = useState<StatusFilter>("ALL");
	const query = useQuery({
		queryKey: ["receipts", "current-month"],
		queryFn: () => api.receipts.list(),
	});

	if (query.isPending) {
		return (
			<div className="grid min-h-80 place-items-center rounded-3xl border border-stone-200 bg-white text-stone-600 shadow-sm">
				<div className="flex items-center gap-3 text-sm font-medium">
					<LoaderCircle className="size-5 animate-spin" />
					Cruzando entradas com o contas a receber…
				</div>
			</div>
		);
	}

	if (query.error) {
		return (
			<div className="rounded-3xl border border-red-200 bg-white p-8 text-center shadow-sm">
				<CircleAlert className="mx-auto mb-3 size-8 text-red-600" />
				<h2 className="font-semibold text-stone-950">Não foi possível consultar o Guardian</h2>
				<p className="mt-2 text-sm text-stone-600">{query.error.message}</p>
			</div>
		);
	}

	const data = query.data;
	const visibleGroups = data.groups.filter((group) => status === "ALL" || group.status === status);

	return (
		<>
			<section className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
				<MetricCard
					label="Entradas bancárias"
					value={FinancePresentation.money(data.summary.total.amountCents)}
					detail={`${data.summary.total.count} movimentos no mês`}
				/>
				<MetricCard
					label="Recebimentos conciliados"
					value={FinancePresentation.money(data.summary.reconciled.amountCents)}
					detail={`${data.summary.reconciled.count} entradas ligadas ao Guardian`}
					tone="success"
				/>
				<MetricCard
					label="Aguardando Guardian"
					value={FinancePresentation.money(data.summary.awaitingGuardian.amountCents)}
					detail={`${data.summary.awaitingGuardian.count} entradas recentes`}
					tone={data.summary.awaitingGuardian.count > 0 ? "attention" : "success"}
				/>
				<MetricCard
					label="Revisar"
					value={FinancePresentation.money(data.summary.reviewRequired.amountCents)}
					detail={`${data.summary.reviewRequired.count} entradas com exceção`}
					tone={data.summary.reviewRequired.count > 0 ? "attention" : "success"}
				/>
				<MetricCard
					label="Fora do contas a receber"
					value={FinancePresentation.money(data.summary.notReceivable.amountCents)}
					detail={`${data.summary.notReceivable.count} transferências ou rendimentos`}
				/>
			</section>

			<section className="mb-6 rounded-3xl border border-emerald-900/10 bg-emerald-950 p-6 text-white shadow-sm">
				<div className="flex items-start gap-4">
					<span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-white/10">
						<ArrowLeftRight className="size-5" />
					</span>
					<div>
						<h2 className="font-semibold">Banco confirma a entrada; Guardian explica a origem</h2>
						<p className="mt-1 max-w-4xl text-sm leading-6 text-emerald-50/75">
							A conciliação liga cada crédito da conta 58000 a uma ou mais baixas do contas a
							receber. Data e valor precisam fechar exatamente, nenhuma baixa pode ser usada duas
							vezes e agrupamentos permanecem visíveis.
						</p>
						<p className="mt-3 text-xs font-medium text-emerald-100/60">
							Guardian: {data.summary.guardian.total.count} baixas ·{" "}
							{FinancePresentation.money(data.summary.guardian.total.amountCents)}
							{data.coverage?.finishedAt &&
								` · Banco atualizado ${FinancePresentation.moment(data.coverage.finishedAt)}`}
						</p>
					</div>
				</div>
			</section>

			<section className="overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm">
				<header className="flex flex-col justify-between gap-4 border-b border-stone-200 px-5 py-5 sm:flex-row sm:items-center">
					<div>
						<h2 className="font-semibold text-stone-950">Entradas do mês</h2>
						<p className="mt-1 text-xs text-stone-500">
							{visibleGroups.reduce((total, group) => total + group.bankTransactions.length, 0)} de{" "}
							{data.summary.total.count} movimentos exibidos
						</p>
					</div>
					<div className="flex flex-wrap gap-1.5">
						<FilterButton active={status === "ALL"} onClick={() => setStatus("ALL")}>
							Todas
						</FilterButton>
						{statusFilters.map((value) => (
							<FilterButton key={value} active={status === value} onClick={() => setStatus(value)}>
								{statusPresentation[value].label}
							</FilterButton>
						))}
					</div>
				</header>

				<div className="divide-y divide-stone-100">
					{visibleGroups.map((group) => (
						<ReceiptRow key={group.id} group={group} />
					))}
					{visibleGroups.length === 0 && (
						<p className="px-6 py-12 text-center text-sm text-stone-500">
							Nenhuma entrada neste estado.
						</p>
					)}
				</div>
			</section>
		</>
	);
}

function ReceiptRow({ group }: { group: ReceiptGroup }) {
	const presentation = statusPresentation[group.status];
	const payerNames = [
		...new Set(group.bankTransactions.flatMap((transaction) => transaction.payerName ?? [])),
	];
	const customerNames = [
		...new Set(group.settlements.map((settlement) => settlement.customerName)),
	];
	const title =
		customerNames.length === 1
			? customerNames[0]
			: customerNames.length > 1
				? `${customerNames.length} clientes no agrupamento`
				: payerNames.length === 1
					? payerNames[0]
					: group.bankTransactions[0].description;

	return (
		<article className="grid gap-4 px-5 py-5 transition hover:bg-stone-50/70 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1.25fr)_auto] lg:items-center">
			<div className="min-w-0">
				<div className="flex flex-wrap items-center gap-2">
					<span
						className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${presentation.className}`}
					>
						{presentation.icon}
						{presentation.label}
					</span>
					<span className="text-xs font-medium text-stone-500">
						{FinancePresentation.transactionDate(group.occurredOn)}
					</span>
				</div>
				<h3 className="mt-3 truncate font-semibold text-stone-950">{title}</h3>
				<p className="mt-1 truncate text-xs text-stone-500">
					{group.bankTransactions.length === 1
						? group.bankTransactions[0].description
						: `${group.bankTransactions.length} movimentos bancários de mesmo valor`}
				</p>
			</div>

			<div className="min-w-0">
				{group.status === "RECONCILED" ? (
					<SettlementDetails group={group} />
				) : (
					<p className="text-sm leading-6 text-stone-600">{reasonText(group)}</p>
				)}
			</div>

			<p className="text-xl font-semibold tracking-[-0.03em] text-stone-950 lg:text-right">
				{FinancePresentation.money(group.amountCents)}
			</p>
		</article>
	);
}

function SettlementDetails({ group }: { group: ReceiptGroup }) {
	if (group.matchKind === "ONE_TO_ONE") {
		const settlement = group.settlements[0];

		return (
			<div className="text-sm">
				<p className="font-medium text-stone-800">Título {settlement.titleNumber}</p>
				<p className="mt-1 text-xs text-stone-500">
					Baixa Guardian
					{settlement.dueOn
						? ` · vencimento ${FinancePresentation.transactionDate(settlement.dueOn)}`
						: ""}
				</p>
			</div>
		);
	}

	return (
		<details className="group rounded-xl border border-stone-200 bg-stone-50 px-3.5 py-3">
			<summary className="cursor-pointer list-none text-sm font-semibold text-stone-800">
				{group.bankTransactions.length} entradas ↔ {group.settlements.length} baixas do Guardian
			</summary>
			<div className="mt-3 space-y-2 border-t border-stone-200 pt-3">
				{group.settlements.map((settlement) => (
					<div key={settlement.id} className="flex items-start justify-between gap-3 text-xs">
						<div className="min-w-0">
							<p className="truncate font-medium text-stone-700">{settlement.customerName}</p>
							<p className="mt-0.5 truncate text-stone-500">Título {settlement.titleNumber}</p>
						</div>
						<p className="shrink-0 font-semibold text-stone-800">
							{FinancePresentation.money(settlement.amountCents)}
						</p>
					</div>
				))}
			</div>
		</details>
	);
}

function reasonText(group: ReceiptGroup) {
	switch (group.reason) {
		case "INTERNAL_TRANSFER":
			return "Transferência entre contas da própria SINDI; não pertence ao contas a receber.";
		case "INVESTMENT_MOVEMENT":
			return "Rendimento ou resgate de aplicação; não representa pagamento de cliente.";
		case "CHECK_DEPOSIT":
			return "Depósito por cheque: número, compensação e baixa precisam ser confrontados separadamente.";
		case "MULTIPLE_GUARDIAN_COMBINATIONS":
			return "Mais de um conjunto de baixas pode explicar este valor; nenhuma combinação foi escolhida.";
		case "NO_GUARDIAN_SETTLEMENT":
			return group.status === "AWAITING_GUARDIAN"
				? "A entrada é recente e ainda não possui uma baixa correspondente no Guardian."
				: "Não foi encontrada uma baixa de mesma data e valor; revisar a origem ou a data registrada.";
		default:
			return "Esta entrada precisa de revisão.";
	}
}

function FilterButton({
	active,
	onClick,
	children,
}: {
	active: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${active ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200 hover:text-stone-900"}`}
		>
			{children}
		</button>
	);
}
